// src/components/ResizableBox.jsx
import React, { useRef, useState, useEffect } from 'react';
import './ResizableBox.css';

const ResizableBox = ({ children, initialX, initialY, initialWidth, initialHeight, onResize, onDrag }) => {
  const boxRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [position, setPosition] = useState({ x: initialX, y: initialY });
  const [dimensions, setDimensions] = useState({ width: initialWidth, height: initialHeight });
  
  // Drag start coordinates
  const dragStart = useRef({ x: 0, y: 0 });
  // Resize start values
  const resizeStart = useRef({ width: 0, height: 0, x: 0, y: 0 });

  // Handle mouse down for dragging
  const handleMouseDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    setIsDragging(true);
    dragStart.current = {
      x: e.clientX - position.x,
      y: e.clientY - position.y
    };
  };

  // Handle mouse down for resizing
  const handleResizeMouseDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    setIsResizing(true);
    resizeStart.current = {
      width: dimensions.width,
      height: dimensions.height,
      x: e.clientX,
      y: e.clientY
    };
  };

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (isDragging) {
        const newX = e.clientX - dragStart.current.x;
        const newY = e.clientY - dragStart.current.y;
        
        setPosition({ x: newX, y: newY });
        
        if (onDrag) onDrag(newX, newY);
      }
      
      if (isResizing) {
        const deltaWidth = e.clientX - resizeStart.current.x;
        const deltaHeight = e.clientY - resizeStart.current.y;
        
        const newWidth = Math.max(50, resizeStart.current.width + deltaWidth);
        const newHeight = Math.max(50, resizeStart.current.height + deltaHeight);
        
        setDimensions({ width: newWidth, height: newHeight });
        
        if (onResize) onResize(newWidth, newHeight);
      }
    };
    
    const handleMouseUp = () => {
      setIsDragging(false);
      setIsResizing(false);
    };
    
    if (isDragging || isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }
    
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, isResizing, onDrag, onResize]);
  
  useEffect(() => {
    setPosition({ x: initialX, y: initialY });
  }, [initialX, initialY]);
  
  useEffect(() => {
    setDimensions({ width: initialWidth, height: initialHeight });
  }, [initialWidth, initialHeight]);

  return (
    <div
      ref={boxRef}
      className="resizable-box"
      style={{
        position: 'absolute',
        left: `${position.x}px`,
        top: `${position.y}px`,
        width: `${dimensions.width}px`,
        height: `${dimensions.height}px`,
        boxSizing: 'border-box',
        zIndex: 200
      }}
    >
      <div
        className="drag-handle"
        onMouseDown={handleMouseDown}
        style={{
          width: '100%',
          height: '100%',
          cursor: 'move'
        }}
      >
        {children}
      </div>
      
      <div
        className="resize-handle"
        onMouseDown={handleResizeMouseDown}
        style={{
          position: 'absolute',
          right: '0',
          bottom: '0',
          width: '16px',
          height: '16px',
          cursor: 'nwse-resize',
          backgroundColor: '#6366f1',
          borderRadius: '50%',
          border: '2px solid white',
          boxShadow: '0 0 4px rgba(0,0,0,0.3)'
        }}
      />
    </div>
  );
};

export default ResizableBox;